import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Theme, // Ensure Theme is imported
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GoogleMap from '../components/GoogleMap'; // Assuming this component exists
import emailjs from '@emailjs/browser';
import { motion } from 'framer-motion';
import { useAppLanguage } from '../hooks/useAppLanguage'; // NEW: Import the language hook

const Contact: React.FC = () => {
  const theme: Theme = useTheme();
  const isMobile: boolean = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useAppLanguage(); // NEW: Initialize the language hook

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    preferredCommunication: 'email',
    message: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [successDialog, setSuccessDialog] = useState(false);

  useEffect(() => {
    emailjs.init('hBv9qZjpP5T5G_0Vr'); // Replace with your EmailJS public key
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialog(false);
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailRegex.test(formData.email)) {
      setSnackbar({
        open: true,
        // NEW: Translate validation message
        message: t('contact.validation.invalidEmail', 'Please enter a valid email address.'),
        severity: 'error',
      });
      return;
    }

    if (formData.phone && !phoneRegex.test(formData.phone)) {
      setSnackbar({
        open: true,
        // NEW: Translate validation message
        message: t('contact.validation.invalidPhone', 'Please enter a valid 10-digit phone number (e.g., 123-456-7890).'),
        severity: 'error',
      });
      return;
    }
    
    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone,
        from_dateOfBirth: formData.dateOfBirth,
        preferred_communication: formData.preferredCommunication,
        message: formData.message,
        // You might want to include the selected language in the email
        // current_language: i18n.language, 
      };

      await emailjs.send(
        'service_5pkrzeo', // Replace with your EmailJS Service ID
        'template_ndc7wdu', // Replace with your EmailJS Template ID
        templateParams
        // Public key is set via emailjs.init() now, not needed here if using latest SDK versions
      );

      setSuccessDialog(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        preferredCommunication: 'email',
        message: '',
      });
    } catch (error) {
      console.error('Error sending email:', error);
      setSnackbar({
        open: true,
        // NEW: Translate error message
        message: t('contact.emailSendError', 'Failed to send message. Please try again later.'),
        severity: 'error',
      });
    }
  };

  return (
    <Box id="contact" 
      sx={{ 
        py: { xs: 6, md: 8 },
        background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
        position: 'relative',
        overflowX: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: 'radial-gradient(circle at 50% 50%, rgba(26,35,126,0.05) 0%, rgba(26,35,126,0) 70%)',
        }
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant={isMobile ? "h4" : "h2"}
            component="h1"
            align="left"
            gutterBottom
            sx={{ 
              mb: { xs: 4, md: 6 },
              px: { xs: 2, md: 0 },
              fontWeight: 700,
              color: 'text.secondary'
            }}
          >
            {/* NEW: Translate page title */}
            {t('contact.pageTitle', 'Contact Us')}
          </Typography>
        </motion.div>

        <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
                <Box sx={{ mt: 2 }}>
                  {/* Address - Address itself is usually not translated */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: 2, md: 2.5 }}}>
                    <Box sx={{ p: 1.5, borderRadius: '50%', backgroundColor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, boxShadow: '0 4px 8px rgba(0,0,0,0.1)'}}>
                      <LocationOnIcon />
                    </Box>
                    <Typography variant={isMobile ? "body1" : "body1"}>
                      20480 Pacifica Drive Suite D
                      <br />
                      Cupertino, CA 95014
                    </Typography>
                  </Box>
                  {/* Phone - Phone number itself is usually not translated */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 2.5 }}}>
                    <Box sx={{ p: 1.5, borderRadius: '50%', backgroundColor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, boxShadow: '0 4px 8px rgba(0,0,0,0.1)'}}>
                      <PhoneIcon />
                    </Box>
                    <Typography variant={isMobile ? "body1" : "body1"}>
                      (408) 869-0998
                    </Typography>
                  </Box>
                  {/* Email - Email address itself is usually not translated */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 2.5 }}}>
                    <Box sx={{ p: 1.5, borderRadius: '50%', backgroundColor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, boxShadow: '0 4px 8px rgba(0,0,0,0.1)'}}>
                      <EmailIcon />
                    </Box>
                    <Typography variant={isMobile ? "body1" : "body1"}>
                      info@pacifica.dental
                    </Typography>
                  </Box>
                  {/* Office Hours - These strings ARE translatable */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start'}}>
                    <Box sx={{ p: 1.5, borderRadius: '50%', backgroundColor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, boxShadow: '0 4px 8px rgba(0,0,0,0.1)'}}>
                      <AccessTimeIcon />
                    </Box>
                    <Typography variant={isMobile ? "body1" : "body1"} sx={{whiteSpace: 'pre-line'}}>
                      {t('contact.officeHours.line1', 'Mon-Fri: 9:00 AM - 6:00 PM')}
                      {'\n'}
                      {t('contact.officeHours.line2', 'Sat: 9:00 AM - 1:00 PM')}
                      {'\n'}
                      {t('contact.officeHours.line3', 'Sun: Closed')}
                    </Typography>
                  </Box>
                </Box>
            </motion.div>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }} // Changed color to primary.main
                >
                  {/* NEW: Translate form title */}
                  {t('contact.form.title', 'Send us a Message')}
                </Typography>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        // NEW: Translate label
                        label={t('contact.form.nameLabel', 'Name')}
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        size={isMobile ? "small" : "medium"}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', '&:hover fieldset': { borderColor: '#1a237e' }}}}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        // NEW: Translate label
                        label={t('contact.form.emailLabel', 'Email')}
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        size={isMobile ? "small" : "medium"}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', '&:hover fieldset': { borderColor: '#1a237e' }}}}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        // Making phone optional based on previous logic (validates only if formData.phone exists)
                        // required 
                        fullWidth
                        // NEW: Translate label
                        label={t('contact.form.phoneLabel', 'Phone')}
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        size={isMobile ? "small" : "medium"}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', '&:hover fieldset': { borderColor: '#1a237e' }}}}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        // NEW: Translate label
                        label={t('contact.form.dateOfBirthLabel', 'Date of Birth')}
                        name="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        size={isMobile ? "small" : "medium"}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', '&:hover fieldset': { borderColor: '#1a237e' }}}}
                      />
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}
                      >
                        {t('contact.form.dateOfBirthHelp', 'Date of birth is needed for booking appointments.')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl 
                        fullWidth 
                        size={isMobile ? "small" : "medium"}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', '&:hover fieldset': { borderColor: '#1a237e' }}}}
                      >
                        <InputLabel id="preferred-communication-label">
                          {/* NEW: Translate label */}
                          {t('contact.form.preferredCommunicationLabel', 'Preferred Communication Method')}
                        </InputLabel>
                        <Select
                          labelId="preferred-communication-label"
                          id="preferred-communication"
                          name="preferredCommunication"
                          value={formData.preferredCommunication}
                          // NEW: Translate label
                          label={t('contact.form.preferredCommunicationLabel', 'Preferred Communication Method')}
                          onChange={handleChange}
                        >
                          {/* NEW: Translate menu items */}
                          <MenuItem value="email">{t('contact.form.communicationOptions.email', 'Email')}</MenuItem>
                          <MenuItem value="phone">{t('contact.form.communicationOptions.phone', 'Phone')}</MenuItem>
                          <MenuItem value="either">{t('contact.form.communicationOptions.either', 'Either')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        // NEW: Translate label
                        label={t('contact.form.messageLabel', 'Message')}
                        name="message"
                        multiline
                        rows={isMobile ? 3 : 4}
                        value={formData.message}
                        onChange={handleChange}
                        size={isMobile ? "small" : "medium"}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', '&:hover fieldset': { borderColor: '#1a237e' }}}}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size={isMobile ? "large" : "large"}
                        sx={{ py: 1.5, borderRadius: '50px', backgroundColor: 'primary.main', boxShadow: '0 3px 5px 2px rgba(26,35,126, .3)', color: "white", '&:hover': { background: 'linear-gradient(45deg, #0d47a1 30%, #1a237e 90%)', transform: 'translateY(-2px)', boxShadow: '0 5px 8px 3px rgba(26,35,126, .4)'}, transition: 'all 0.3s ease' }}
                      >
                        {/* NEW: Translate button text */}
                        {t('contact.form.submitButton', 'Send Message')}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
            </motion.div>
          </Grid>

          {/* Google Map */}
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Box sx={{ mt: { xs: 4, md: 6 } }}>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  gutterBottom
                  sx={{ mb: { xs: 2, md: 3 }, fontWeight: 600, color: 'primary.dark' }} // Changed color
                >
                  {/* NEW: Translate section title */}
                  {t('contact.map.title', 'Find Us')}
                </Typography>
                <Paper sx={{ borderRadius: '20px', overflow: 'hidden', boxShadow: '0 4px 8px rgba(0,0,0,0.05)'}}>
                  <GoogleMap />
                </Paper>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Success Dialog */}
      <Dialog
        open={successDialog}
        onClose={handleCloseSuccessDialog}
        aria-labelledby="success-dialog-title"
        aria-describedby="success-dialog-description"
        PaperProps={{ sx: { borderRadius: '20px', padding: 2, maxWidth: '400px', width: '90%', background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)', boxShadow: '0 8px 16px rgba(0,0,0,0.1)'}}}
      >
        <DialogTitle 
          id="success-dialog-title"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main', pb: 1, fontWeight: 600 }}
        >
          <CheckCircleIcon sx={{ fontSize: 32 }} />
          {/* NEW: Translate dialog title */}
          {t('contact.successDialog.title', 'Message Sent Successfully!')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="success-dialog-description" sx={{ lineHeight: 1.6 }}>
            {/* NEW: Translate dialog content */}
            {t('contact.successDialog.message', 'Thank you for contacting us. We have received your message and will get back to you as soon as possible.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseSuccessDialog} 
            variant="contained"
            sx={{ px: 3, py: 1, borderRadius: '50px', backgroundColor: 'primary.main', boxShadow: '0 3px 5px 2px rgba(26,35,126, .3)', '&:hover': { background: 'linear-gradient(45deg, #0d47a1 30%, #1a237e 90%)', transform: 'translateY(-2px)', boxShadow: '0 5px 8px 3px rgba(26,35,126, .4)'}, transition: 'all 0.3s ease' }}
          >
            {/* NEW: Translate button text */}
            {t('contact.successDialog.closeButton', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: '10px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)'}}
        >
          {snackbar.message} {/* Snackbar message is already being set with t() */}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
